import { useState, useEffect, useRef } from 'react';
import References from '../../../helpers/Reference';

import { CustomDatepicker } from '../../datepicker/custom.datepicker';
import dayjs from 'dayjs';
import L from 'leaflet';
import turf from 'turf';

import { Space, Button, Dropdown, Popover } from 'antd';
import { ReloadOutlined, BackwardFilled, ForwardOutlined, PlayCircleOutlined, PauseCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';

import { downTo10MinutesRound } from '../../../helpers/helpers';

import { getLegendTitle, loadFields, parseFields, polygonTransperent } from './utils';
import { MAX } from './utils';
import './precipitation.fields.player.css';

const PRECIPITATION_FIELD_PERIOD = 3;
const PRECIPITATION_FIELD_PERIOD_CALC = 72;

const COLOR_POLYGON_FILL = '#003';
const COLOR_POLYGON_BORDER = '#00f';
const OPACITY_POLYGON_BORDER = 0.1;

const ITEMS_AGG = [
    {
      label: '10 минут',
      key: '10M',
    },
    {
      label: '3 часа',
      key: '3H',
    }
  ];    


const l = L.featureGroup();

export const PrecipitationFieldsPlayer = ({mapRef}) => {

    const [isLoading, setIsLoading] = useState(false);
    const [start, setStart] = useState(downTo10MinutesRound(dayjs().subtract(PRECIPITATION_FIELD_PERIOD_CALC, 'hour')));
    const [finish, setFinish] = useState(downTo10MinutesRound(dayjs()));

    const [currFrame, setCurrFrame] = useState(0);
    const [isLast, setIsLast] = useState( false );

    const [data, setData ] = useState([]);    

    const [progress, setProgress] = useState(0);
    const [isPlay, setIsPlay] = useState(false);
    const [playTimer, setPlayTimer] = useState(null);

    const [periodAgg, setPeriodAgg] = useState(ITEMS_AGG[0].key);
    const [periodAggName, setPeriodAggName] = useState(ITEMS_AGG[0].label);

    const forwardRef = useRef();

    useEffect(() => {
        return () => {
            onClear();
        }
    }, []);

    const onDatepickerStartOk = (value) => {
        setStart(value);        
        if (value.isAfter(dayjs().subtract(PRECIPITATION_FIELD_PERIOD_CALC, 'hour')))
            setFinish(downTo10MinutesRound(dayjs()));            
        else            
            setFinish(dayjs(value).add(PRECIPITATION_FIELD_PERIOD, 'hour'));                    
        
        if (!isAggregatedPeriod(value))
            onPeriodAggClick(ITEMS_AGG[0]);
    }


    const onDatepickerFinishOk = (value) => {
        setFinish(value);
    }

    const onStart = () => {
        onStop();        
        setIsLoading( true );    
        return loadFields(start, finish, mapRef.current.getBounds(), mapRef.current.getZoom(), periodAgg)        
        .then((data) => {
            const parseData = parseFields(data); 
            setData( parseData );
            setCurrFrame(0);            
            setIsLast(false);
            onDraw(parseData, 0);            
        })
        .catch(() => console.log('Error precipitation field'))
        .finally(() => {
            setIsLoading(false)
        });    
    }

    const onDraw = (fileds, frame ) => {

        if (!fileds.hasOwnProperty(frame))
            return false;

        if (fileds.length <= (frame - 1) )
            return false;

        onClear();
        setProgress( frame  * 100 / (fileds.length - 1));

        const map = mapRef.current;
        const item = fileds[frame];
        
        if (item.features.length > 0) {
            item.features.forEach((feature, index) => {                
                feature = turf.flip( feature );               
                const opacity = `${polygonTransperent(feature?.properties?.min_val, periodAgg)}%`;
                const tooltip = showPrecIntensity(feature.properties);

                if (feature.geometry?.type === 'MultiPolygon' || feature.geometry?.type === 'Polygon') {                  
                    const polygon = L.polygon([feature?.geometry.coordinates], {                                        
                        stroke: true,
                        weight: 1,
                        color: COLOR_POLYGON_BORDER,
                        opacity: OPACITY_POLYGON_BORDER,                        

                        fill: true,                        
                        fillColor: COLOR_POLYGON_FILL,                    
                        fillOpacity: opacity,

                    })
                    .bindTooltip(tooltip, {sticky: true});    
             
                    l.addLayer(polygon);
                }
            })
        }
        l
            .bringToFront()
            .addTo(map);            
    }   

    const onForward = () => {      
        const frame = currFrame + 1;        
        if (frame <= data.length - 1 ) {     
            setCurrFrame(frame);            
            onDraw( data, frame );

            setIsLast(frame === data.length - 1)
        }
    }

    const onBackward = () => {
        setIsLast(false);
        const frame = currFrame - 1;
        if (frame >= 0) {         
            setCurrFrame(frame);            
            onDraw( data, frame );
        }
    }

    const onPlayerProgressClick = (e) => {
        const totalFrames = data.length - 1;
        const pos = (e.clientX - e.currentTarget.offsetLeft) * 100 / e.target.getBoundingClientRect().width;
        let frame = Math.floor(totalFrames * pos / 100);
        frame = pos > 50 ? Math.ceil(frame) : Math.floor(frame);                
        ++frame;
        //поправиочка на нажатие возле начала ** чистый хардкор
        if (frame === 1 && pos < 3)
            frame = 0;
        setCurrFrame(frame);
        setIsLast(frame === totalFrames);   
        onDraw(data, frame);     
    }

    const onRestart = () => {
        setPlayTimer(clearTimeout( playTimer ));
        setIsPlay(false);
        setIsLast(false);
        setCurrFrame(0);
        setProgress(0);
    }

    const onReload = () => {
        onRestart();
        onDraw(data, 0);
    }

    const onPlay = () => {   
        const isPlayed = !isPlay;
        let currentFrame = currFrame;
        let totalFrames = data.length;
        setIsPlay(isPlayed);        

        if ( isPlayed ) {
                const playTimer = setInterval(() => {
                if (currentFrame <= totalFrames - 1) {                                        
                    ++currentFrame; 
                    forwardRef.current.click();
                } else {
                    setIsPlay(false);
                    setPlayTimer(clearTimeout( playTimer ));                                    
                }
            }, 500);            
            setPlayTimer(playTimer);
        } else {
            onStop();
        }
    }

    const onStop = () => {
        setIsPlay(false);
        setPlayTimer(clearTimeout( playTimer ));                            
    }
    
    const onClear = () => {
        l.clearLayers();
    }    

    const onPeriodAggClick = ( item ) => {
        const agg = isAggregatedPeriod(start) ? ITEMS_AGG.filter((val) => val.key === item.key)[0] : ITEMS_AGG[0];
    
        setPeriodAgg(agg.key);
        setPeriodAggName(agg.label);
        onClear();
        onRestart();
        setData([]);  
    }      

    const getPlayIcon = () => {
        if (isLast)
            return <ReloadOutlined />;
        return isPlay ? <PauseCircleOutlined /> : <PlayCircleOutlined />;       
    }

    const showPrecIntensity = (prec) => {
        let ret = '';
        if (prec.min_val === 0)
            ret = ` < ${prec.max_val}${References.mchs.precipitation.unit}`;
        else if (prec.max_val === null)
            ret = ` > ${prec.min_val}${References.mchs.precipitation.unit}`;
        else
            ret = `${prec.min_val}${References.mchs.precipitation.unit} — ${prec.max_val}${References.mchs.precipitation.unit}`;
        return `<div class="ml-2 mr-2">${ret}</div>`;        
    }

    const isAggregatedPeriod = (start) => {
        return Math.floor(dayjs().diff(start) / 1000 / 3600) <= PRECIPITATION_FIELD_PERIOD_CALC + 1;
    }

    return (
        <>
        <div id="precipitation-fields-player">
                {isLoading && 
                    <div className="precipitation-fields-loading"></div>
                }

            <div className="datepicker">
            <Space>
            <Dropdown 
                menu={{items: ITEMS_AGG, onClick: onPeriodAggClick}} 
                trigger={['click']}
                className="dropdown-prec-agg-wrap"
            >
            <a onClick={e => e.preventDefault()}>
                <Space>
                    {periodAggName}
                    <UpOutlined />
                </Space>
            </a>                
            </Dropdown>                
            <CustomDatepicker 
                    isCustom={false} start={start} finish={finish} onStart={onDatepickerStartOk} onFinish={onDatepickerFinishOk}
                    disabledFinish={true}
                    className="small-input"
                    props={{
                        format: References.dateHourMinuteFormat,
                        time_format: "HH:mm",                        
                    }}
                />    
                <Button type="default" shape="circle" icon={<ReloadOutlined />} onClick={onStart}/>                
            </Space>
            </div>
            { data.length > 0 &&
            <>
            <div className="dj ml-2">
                <Space>
                    <Button type="default" icon={<BackwardFilled />} onClick={onBackward}/>
                    <Button type="default" icon={getPlayIcon()} onClick={isLast ? onReload : onPlay}/>
                    <Button type="default" icon={<ForwardOutlined />} onClick={onForward} ref={forwardRef}/>
                </Space>
            </div>
            <div className="player ml-2" onClick={onPlayerProgressClick}>
                <div className="player-position" style={{width: `${progress}%`}}></div>
                <div className="player-current-frame">{dayjs(start).add(currFrame * 10, 'minute').format('DD.MM HH:mm')}</div>                                    
            </div>
            </>            
            }            
        </div>

        <div id="precipitation-legend">
            {MAX[periodAgg].map((val, idx) => {                
                    return (
                        <Popover
                            content={getLegendTitle(periodAgg, idx)}
                            trigger={["hover", "click"]}
                            placement="left"
                        >
                        <div style={{backgroundColor: COLOR_POLYGON_FILL, opacity: `${polygonTransperent(val, periodAgg)}%`}}></div>
                        </Popover>                    
                    )

            })}            
        </div>            

        </>
    );
}